if (!customElements.get('finch-search-overlay')) {
  class FinchSearchOverlay extends HTMLElement {
    constructor() {
      super();
      this.cache = new Map();
      this.debounceTimer = undefined;
      this.requestController = undefined;
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.dialog = this.querySelector('[data-fso-dialog]');
      this.openButton = this.querySelector('[data-fso-open]');
      this.closeButton = this.querySelector('[data-fso-close]');
      this.form = this.querySelector('[data-fso-form]');
      this.input = this.querySelector('[data-fso-input]');
      this.resetButton = this.querySelector('[data-fso-reset]');
      this.defaultPanel = this.querySelector('[data-fso-default]');
      this.resultsPanel = this.querySelector('[data-fso-results]');
      this.status = this.querySelector('[data-fso-status]');
      this.featuredGrid = this.querySelector('.fso-product-grid');
      this.featuredProducts = Array.from(this.querySelectorAll('[data-fso-featured-product]'));
      this.featuredRotationOffset = 0;
      this.headerRoot = this.closest('adaptable-header');
      this.headerWrapper = this.closest('.section-header');

      if (!this.dialog || !this.form || !this.input) return;

      this.openButton?.addEventListener('click', () => {
        if (this.dialog.open) this.close();
        else this.open();
      }, { signal: this.signal });
      this.closeButton?.addEventListener('click', () => this.close(), { signal: this.signal });
      this.dialog.addEventListener('close', () => this.afterClose(), { signal: this.signal });
      document.addEventListener('pointerdown', (event) => this.onDocumentPointerDown(event), { signal: this.signal });
      document.addEventListener('keydown', (event) => this.onDocumentKeydown(event), { signal: this.signal });
      window.addEventListener('resize', () => this.updatePosition(), { signal: this.signal, passive: true });
      window.addEventListener('scroll', () => this.updatePosition(), { signal: this.signal, passive: true });
      this.input.addEventListener('input', () => this.onInput(), { signal: this.signal });
      this.form.addEventListener('reset', () => window.requestAnimationFrame(() => this.reset()), { signal: this.signal });
      this.form.addEventListener('submit', (event) => {
        if (this.input.value.trim()) return;
        event.preventDefault();
        this.input.focus();
      }, { signal: this.signal });
      document.addEventListener('shopify:section:unload', (event) => {
        if (event.target?.contains(this)) this.disconnectedCallback();
      }, { signal: this.signal });

      if ('ResizeObserver' in window && this.headerRoot) {
        this.positionObserver = new ResizeObserver(() => this.updatePosition());
        this.positionObserver.observe(this.headerRoot);
      }

      this.syncResetButton();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.requestController?.abort();
      this.positionObserver?.disconnect();
      window.clearTimeout(this.debounceTimer);
      this.headerWrapper?.classList.remove('menu-open');
      this.openButton?.setAttribute('aria-expanded', 'false');
      this.initialized = false;
    }

    open() {
      if (this.dialog.open) return;
      this.returnFocusTarget = document.activeElement;
      this.rotateFeaturedProducts();
      this.updatePosition();
      this.dialog.show();
      this.openButton?.setAttribute('aria-expanded', 'true');
      this.headerWrapper?.classList.add('menu-open');
      window.requestAnimationFrame(() => {
        this.updatePosition();
        this.input.focus({ preventScroll: true });
        this.input.select();
        if (this.input.value.trim()) this.onInput();
      });
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }

    afterClose() {
      this.openButton?.setAttribute('aria-expanded', 'false');
      this.headerWrapper?.classList.remove('menu-open');
      window.clearTimeout(this.debounceTimer);
      this.requestController?.abort();
      this.activeQuery = '';
      this.setLoading(false);
      if (this.returnFocusTarget instanceof HTMLElement && this.returnFocusTarget.isConnected) {
        this.returnFocusTarget.focus({ preventScroll: true });
      } else {
        this.openButton?.focus({ preventScroll: true });
      }
    }

    onDocumentPointerDown(event) {
      if (!this.dialog.open) return;
      const path = event.composedPath();
      if (path.includes(this.dialog) || path.includes(this.openButton)) return;
      this.close();
    }

    onDocumentKeydown(event) {
      if (event.key !== 'Escape' || !this.dialog.open) return;
      event.preventDefault();
      this.close();
    }

    updatePosition() {
      if (!this.dialog?.open && !this.headerRoot) return;
      const headerBottom = this.headerRoot?.getBoundingClientRect().bottom || 0;
      const panelTop = Math.max(0, Math.round(headerBottom));
      this.dialog?.style.setProperty('--fso-panel-top', `${panelTop}px`);
    }

    rotateFeaturedProducts() {
      const products = this.featuredProducts;
      if (!this.featuredGrid || products.length === 0) return;

      const configuredLimit = Number.parseInt(this.dataset.featuredVisibleLimit, 10);
      const visibleLimit = Math.min(
        products.length,
        Number.isFinite(configuredLimit) && configuredLimit > 0 ? configuredLimit : 5
      );
      const storageKey = `finch-search-featured-offset:${this.dialog?.id || 'default'}`;
      let offset = this.featuredRotationOffset % products.length;

      try {
        const storedOffset = Number.parseInt(window.sessionStorage.getItem(storageKey), 10);
        if (Number.isFinite(storedOffset)) offset = storedOffset % products.length;
      } catch (_error) {
        // Rotation still works in memory when session storage is unavailable.
      }

      const orderedProducts = products.map((_, index) => products[(offset + index) % products.length]);
      const fragment = document.createDocumentFragment();
      orderedProducts.forEach((product, index) => {
        product.hidden = index >= visibleLimit;
        fragment.append(product);
      });
      this.featuredGrid.append(fragment);

      const nextOffset = (offset + visibleLimit) % products.length;
      this.featuredRotationOffset = nextOffset;
      try {
        window.sessionStorage.setItem(storageKey, String(nextOffset));
      } catch (_error) {
        // No persistence is required for the in-memory fallback.
      }
    }

    onInput() {
      this.syncResetButton();
      window.clearTimeout(this.debounceTimer);
      this.requestController?.abort();
      this.hideStaleResults();
      const query = this.input.value.trim();
      this.activeQuery = query.toLocaleLowerCase(document.documentElement.lang || undefined);
      if (!query) {
        this.showDefault();
        return;
      }

      this.setLoading(true);
      this.debounceTimer = window.setTimeout(() => this.fetchResults(query), 300);
    }

    async fetchResults(query) {
      const cacheKey = query.toLocaleLowerCase(document.documentElement.lang || undefined);
      if (cacheKey !== this.activeQuery) return;
      if (this.cache.has(cacheKey)) {
        this.renderResults(this.cache.get(cacheKey));
        return;
      }

      this.requestController?.abort();
      this.requestController = new AbortController();

      try {
        const url = new URL(this.dataset.predictiveSearchUrl, window.location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', 'product,collection,query');
        url.searchParams.set('resources[limit]', '10');
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('resources[options][unavailable_products]', 'last');
        url.searchParams.set('section_id', this.dataset.predictiveSectionId || 'finch-predictive-search');

        const response = await fetch(url.toString(), {
          headers: { Accept: 'text/html' },
          signal: this.requestController.signal,
        });
        if (!response.ok) throw new Error(`Predictive search failed with ${response.status}`);

        const html = await response.text();
        if (cacheKey !== this.activeQuery) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const section = parsed.querySelector(`#shopify-section-${this.dataset.predictiveSectionId}`);
        if (!section) throw new Error('Predictive search section is missing');

        const markup = section.innerHTML;
        this.cache.set(cacheKey, markup);
        this.renderResults(markup);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.setLoading(false);
        this.announce(this.dataset.errorText || 'Zoeken is tijdelijk niet beschikbaar. Druk op Enter om verder te zoeken.');
      }
    }

    renderResults(markup) {
      if (!this.resultsPanel) return;
      this.resultsPanel.innerHTML = markup;
      this.defaultPanel.hidden = true;
      this.resultsPanel.hidden = false;
      this.applyEditableCopy();
      this.setLoading(false);

      const resultRoot = this.resultsPanel.querySelector('[data-finch-predictive-results]');
      const linkCount = this.resultsPanel.querySelectorAll('[data-search-option]').length;
      const count = Number(resultRoot?.dataset.resultsCount || linkCount);
      const template = this.dataset.resultsTemplate || '{count} zoekresultaten beschikbaar.';
      this.announce(template.replace('{count}', count));
    }

    applyEditableCopy() {
      const copy = [
        ['[data-heading-products]', this.dataset.productsHeading],
        ['[data-heading-suggestions]', this.dataset.suggestionsHeading],
        ['[data-heading-collections]', this.dataset.collectionsHeading],
        ['[data-empty-message]', this.dataset.noResultsText],
      ];
      copy.forEach(([selector, value]) => {
        if (!value) return;
        this.resultsPanel.querySelectorAll(selector).forEach((element) => {
          element.textContent = value;
        });
      });
    }

    showDefault() {
      this.requestController?.abort();
      this.activeQuery = '';
      this.setLoading(false);
      if (this.defaultPanel) this.defaultPanel.hidden = false;
      if (this.resultsPanel) {
        this.resultsPanel.hidden = true;
        this.resultsPanel.innerHTML = '';
      }
      this.announce('');
    }

    hideStaleResults() {
      if (this.defaultPanel) this.defaultPanel.hidden = true;
      if (this.resultsPanel) {
        this.resultsPanel.hidden = true;
        this.resultsPanel.innerHTML = '';
      }
    }

    reset() {
      this.input.value = '';
      this.syncResetButton();
      this.showDefault();
      this.input.focus();
    }

    syncResetButton() {
      this.resetButton?.classList.toggle('is-hidden', this.input.value.length === 0);
    }

    setLoading(isLoading) {
      this.toggleAttribute('data-loading', isLoading);
      this.input.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      if (isLoading) this.announce(this.dataset.loadingText || 'Zoeken…');
    }

    announce(message) {
      if (!this.status) return;
      this.status.textContent = '';
      window.requestAnimationFrame(() => {
        this.status.textContent = message;
      });
    }

  }

  customElements.define('finch-search-overlay', FinchSearchOverlay);
}
